<template>
  <div class="transactions-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <!-- Filters -->
      <recognition-filters
        :programs="programs"
        :program-id="programId"
        :selected-program="selectedProgram"
        :selected-date-range="selectedDateRange"
        :date-range-options="dateRangeOptions"
        :has-active-filters="hasActiveFilters"
        @update:selected-program="selectedProgram = $event"
        @update:selected-date-range="selectedDateRange = $event"
        @reset="resetFilters"
      >
        <template #prepend>
          <b-form-select v-model="selectedCategory" :options="categoryOptions" class="recognition-filters__filter" />
        </template>
      </recognition-filters>

      <b-table
        v-if="transactions.length > 0"
        :items="transactions"
        :fields="TRANSACTIONS_TABLE_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="transactions-table__table"
      >
        <template #cell(datetime_of_transaction)="{ item }">
          <span>{{ formatDate(item.datetime_of_transaction) }}</span>
        </template>

        <template #cell(category)="{ item }">
          <span>{{ formatCategory(item.category) }}</span>
        </template>

        <template #cell(program_name)="{ item }">
          <span>{{ item.program_name || '-' }}</span>
        </template>

        <template #cell(nominee_name)="{ item }">
          <span>{{ item.nominee_name || '-' }}</span>
        </template>

        <template #cell(employee_code)="{ item }">
          <span>{{ item.employee_code || '-' }}</span>
        </template>

        <template #cell(cash_reward)="{ item }">
          <template v-if="item.valid_grant_types && item.valid_grant_types.length > 0">
            <span
              :id="`cash-reward-popover-${item._uid}`"
              :class="[
                'transactions-table__cash-reward',
                { 'transactions-table__cash-reward--yes': hasCashReward(item) },
              ]"
            >
              {{ hasCashReward(item) ? 'Yes' : 'No' }}
            </span>
            <b-popover :target="`cash-reward-popover-${item._uid}`" triggers="hover focus" placement="bottom">
              <div class="transactions-table__popover-container">
                <div class="transactions-table__popover-title">Redeem options:</div>
                <div v-for="grantType in item.valid_grant_types" :key="grantType">
                  {{ formatGrantType(grantType) }}
                </div>
              </div>
            </b-popover>
          </template>
          <template v-else>
            <span class="text-muted">-</span>
          </template>
        </template>

        <template #cell(amount_minor)="{ item }">
          <span :class="getAmountClass(item)">
            {{ formatAmount(item) }}
          </span>
        </template>

        <template #cell(balance_after_minor)="{ item }">
          <span>{{ formatCurrency(item.balance_after_minor) }}</span>
        </template>
      </b-table>

      <div v-else class="transactions-table__empty">
        <p class="text-muted">No transactions found.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
import AppLoading from '@/components/AppLoading.vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import { toCurrency } from '@/utils';
import { getDateFromForRange } from '@/pages/recognition/constants';
import ApiClient from '@/ApiClient';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
});

const transactions = ref([]);
const loading = ref(false);
const fetchRequestId = ref(0);

const selectedCategory = ref(null);
const selectedProgram = ref(null);
const selectedDateRange = ref('last_30_days');

const TRANSACTIONS_TABLE_FIELDS = [
  {
    key: 'datetime_of_transaction',
    label: 'Date',
  },
  {
    key: 'category',
    label: 'Category',
  },
  {
    key: 'program_name',
    label: 'Award',
  },
  {
    key: 'nominee_name',
    label: 'Employee',
  },
  {
    key: 'employee_code',
    label: 'Employee No',
  },
  {
    key: 'cash_reward',
    label: 'Cash reward',
  },
  {
    key: 'amount_minor',
    label: 'Amount',
  },
  {
    key: 'balance_after_minor',
    label: 'Balance',
  },
];

const categoryOptions = [
  { value: null, text: 'Category' },
  { value: 'DEPOSIT', text: 'Deposit' },
  { value: 'AWARD_GRANTED', text: 'Award granted' },
  { value: 'REWARD_EXPIRED', text: 'Reward expired' },
];

const dateRangeOptions = [
  { value: 'last_30_days', text: 'Last 30 days' },
  { value: 'last_90_days', text: 'Last 90 days' },
  { value: 'last_year', text: 'Last year' },
  { value: null, text: 'All time' },
];

const currency = computed(() => State.state?.company?.default_currency);

const hasActiveFilters = computed(() => {
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  return selectedCategory.value !== null || hasProgramFilter || selectedDateRange.value !== 'last_30_days';
});

const fetchTransactions = async () => {
  fetchRequestId.value += 1;
  const currentRequestId = fetchRequestId.value;

  loading.value = true;
  try {
    const params = {};
    if (selectedCategory.value) {
      params.category = selectedCategory.value;
    }
    if (props.programId) {
      params.recognition_award_program_id = props.programId;
    } else if (selectedProgram.value) {
      params.recognition_award_program_id = selectedProgram.value;
    }
    const dateFrom = getDateFromForRange(selectedDateRange.value);
    if (dateFrom) {
      params.date_from = dateFrom;
    }
    const result = await ApiClient.getLedgerTransactions(params);

    if (currentRequestId !== fetchRequestId.value) {
      return;
    }

    transactions.value = (result.transactions || []).map((txn, index) => ({
      ...txn,
      _uid: `${txn.datetime_of_transaction}-${txn.category}-${index}`,
    }));
  } catch (e) {
    if (currentRequestId !== fetchRequestId.value) {
      return;
    }
    transactions.value = [];
  } finally {
    if (currentRequestId === fetchRequestId.value) {
      loading.value = false;
    }
  }
};

const resetFilters = () => {
  selectedCategory.value = null;
  if (!props.programId) {
    selectedProgram.value = null;
  }
  selectedDateRange.value = 'last_30_days';
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return moment(dateStr).format('D MMM YYYY');
};

const formatCategory = category => {
  const categoryMap = {
    DEPOSIT: 'Deposit',
    AWARD_GRANTED: 'Award granted',
    REWARD_EXPIRED: 'Reward expired',
  };
  return categoryMap[category] || category;
};

const formatCurrency = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return toCurrency(value, currency.value);
};

const hasCashReward = item => {
  return item.valid_grant_types?.includes('BANK_ACCOUNT') || item.valid_grant_types?.includes('WPS');
};

const formatGrantType = grantType => {
  const grantTypeMap = {
    VOUCHER: 'Gift card',
    BANK_ACCOUNT: 'Cash added to salary',
    WPS: 'Savings pot',
  };
  return grantTypeMap[grantType] || grantType;
};

const getAmountClass = item => {
  if (item.category === 'DEPOSIT' || item.category === 'REWARD_EXPIRED') {
    return 'transactions-table__amount--positive';
  }
  return '';
};

const formatAmount = item => {
  const value = item.amount_minor;
  if (value === null || value === undefined) {
    return '-';
  }
  const formatted = toCurrency(value, currency.value);
  if (item.category === 'DEPOSIT' || item.category === 'REWARD_EXPIRED') {
    return `+${formatted}`;
  }
  return `-${formatted}`;
};

// Watch filters and refetch
watch([selectedCategory, selectedProgram, selectedDateRange], () => {
  fetchTransactions();
});

onMounted(() => {
  fetchTransactions();
});
</script>

<style scoped lang="scss">
.transactions-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__cash-reward {
    cursor: pointer;

    &--yes {
      color: var(--palette-color-brand-primary);
      text-decoration: underline;
    }
  }

  &__amount--positive {
    color: var(--palette-color-success);
  }

  &__popover-container {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__popover-title {
    font-weight: 500;
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
